import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../states/hooks";
import {
  fetchUsers,
  selectUsers,
  selectIsLoading,
  selectError,
  selectTotalCount,
  selectCurrentPage,
  selectPageSize,
  setPage,
  setPageSize,
  createUser,
  updateUser,
  clearError,
  User,
} from "../../states/users/UsersSlice";
import { useBase } from "../../context/BaseContext";
import EditUserModal from "../../components/EditUserModal";
import {
  UsersIcon,
  EmptyBoxIcon,
  EditIcon,
  DeleteIcon,
} from "../../assets/svg";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Avatar,
  Chip,
  Box,
  Typography,
  TablePagination,
  Button,
} from "@mui/material";
import "./Dashboard.css";

const DashboardPage = () => {
  const dispatch = useAppDispatch();
  const users = useAppSelector(selectUsers);
  const isLoading = useAppSelector(selectIsLoading);
  const error = useAppSelector(selectError);
  const totalCount = useAppSelector(selectTotalCount);
  const currentPage = useAppSelector(selectCurrentPage);
  const pageSize = useAppSelector(selectPageSize);
  const { showError } = useBase();

  // Modal state
  const [editModal, setEditModal] = useState<{
    isOpen: boolean;
    user: User | null;
  }>({ isOpen: false, user: null });

  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  useEffect(() => {
    console.log("Users data:", users); // Debug log
  }, [users]);

  // Fetch users on component mount or when search/pagination changes
  useEffect(() => {
    console.log(
      "Fetching users for page:",
      currentPage,
      "with page size:",
      pageSize,
      "search:",
      debouncedSearchTerm
    ); // Debug log
    dispatch(
      fetchUsers({
        page: currentPage,
        pageSize,
        searchKey: debouncedSearchTerm,
      })
    );
  }, [dispatch, currentPage, pageSize, debouncedSearchTerm]);

  // Show error notification
  useEffect(() => {
    if (error) {
      showError("Error", error);
      dispatch(clearError());
    }
  }, [error, showError, dispatch]);

  const handlePageChange = (newPage: number) => {
    dispatch(setPage(newPage));
  };

  const handleEditUser = (userId: string) => {
    const user = users.find((u) => u.id === userId);
    if (user) {
      console.log("Editing user:", user);
      setEditModal({ isOpen: true, user });
    }
  };

  const handleCreateUser = () => {
    console.log("Creating new user");
    setEditModal({ isOpen: true, user: null });
  };

  const handleCloseModal = () => {
    setEditModal({ isOpen: false, user: null });
  };

  const handleSaveUser = async (data: {
    name: string;
    userName?: string;
    email?: string;
    password: string;
    profiles: any[];
  }) => {
    if (editModal.user) {
      console.log("Updating user with edited data:", {
        userId: editModal.user.id,
        name: data.name,
        password: data.password,
        profiles: data.profiles,
      });
      await dispatch(
        updateUser({
          id: editModal.user.id,
          name: data.name,
          password: data.password,
          profiles: data.profiles,
        })
      );
    } else {
      console.log("Creating new user with data:", {
        name: data.name,
        userName: data.userName || "",
        email: data.email || "",
        password: data.password,
        profiles: data.profiles,
      });
      await dispatch(
        createUser({
          name: data.name,
          userName: data.userName || "",
          email: data.email || "",
          password: data.password,
          profiles: data.profiles,
          phoneNumber: data.userName || "",
        })
      );
    }
    handleCloseModal();
    // Refresh the user list
    dispatch(fetchUsers({ page: currentPage, pageSize }));
  };

  const handleDeleteUser = (userId: string) => {
    console.log("Deleting user:", userId);
    // TODO: Implement delete functionality with confirmation
    const user = users.find((u) => u.id === userId);
    if (user) {
      const confirmed = window.confirm(
        `Are you sure you want to delete user: ${user.name}?`
      );
      if (confirmed) {
        // Call delete API here
        console.log("User deleted:", userId);
        // dispatch(deleteUser(userId));
      }
    }
  };

  return (
    <>
      <EditUserModal
        isOpen={editModal.isOpen}
        user={editModal.user}
        onClose={handleCloseModal}
        onSave={handleSaveUser}
      />

      <div className="dashboard-container">
        {/* <div className="dashboard-header">
          <h1 className="dashboard-title">Dashboard</h1>
          <p className="dashboard-subtitle">
            Manage and view all users in the system
          </p>
        </div> */}

        {/* Stats */}
        <div className="dashboard-stats-grid">
          <div className="dashboard-stat-card">
            <div className="dashboard-stat-card-content">
              <div className="dashboard-stat-flex">
                <div className="dashboard-stat-icon-wrapper">
                  <UsersIcon className="dashboard-stat-icon" />
                </div>
                <div className="dashboard-stat-info">
                  <dl>
                    <dt className="dashboard-stat-label">Total Users</dt>
                    <dd className="dashboard-stat-value">{totalCount}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div></div>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateUser}
            sx={{
              textTransform: "none",
              height: "fit-content",
              alignSelf: "center",
            }}
          >
            + Add New User
          </Button>
        </div>

        {/* Users Table */}
        <div className="dashboard-table-container">
          <div className="dashboard-table-header">
            {/* <h3 className="dashboard-table-title">Users List</h3> */}
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: "8px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                width: "300px",
                outline: "none",
              }}
            />
          </div>

          {isLoading ? (
            <div className="dashboard-loading">
              <div className="dashboard-loading-content">
                <div className="dashboard-loading-spinner"></div>
                <p className="dashboard-loading-text">Loading users...</p>
              </div>
            </div>
          ) : users.length === 0 ? (
            <div className="dashboard-empty">
              <EmptyBoxIcon className="dashboard-empty-icon" />
              <h3 className="dashboard-empty-title">
                {searchTerm ? "No matching users" : "No users"}
              </h3>
              <p className="dashboard-empty-description">
                {searchTerm
                  ? `No users found matching "${searchTerm}"`
                  : "No users found in the system."}
              </p>
            </div>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
                <Table sx={{ minWidth: 650 }} aria-label="users table">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                      <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        UserName
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Profile</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }} align="center">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow
                        key={user.id}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                          "&:hover": { backgroundColor: "#f9f9f9" },
                        }}
                      >
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Avatar sx={{ bgcolor: "#3b82f6" }}>
                              {user.name?.charAt(0).toUpperCase() || "U"}
                            </Avatar>
                            <Typography>{user.name || "N/A"}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.userName || "N/A"}</TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.5,
                            }}
                          >
                            {user.profile &&
                              user.profile.map((p) => (
                                <Chip
                                  key={p.id}
                                  label={p.profile_name}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              ))}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            onClick={() => handleEditUser(user.id)}
                            color="primary"
                            size="small"
                            title="Edit user"
                          >
                            <EditIcon
                              style={{ width: "20px", height: "20px" }}
                            />
                          </IconButton>
                          {/* <IconButton
                            onClick={() => handleDeleteUser(user.id)}
                            color="error"
                            size="small"
                            title="Delete user"
                          >
                            <DeleteIcon
                              style={{ width: "20px", height: "20px" }}
                            />
                          </IconButton> */}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={currentPage - 1}
                  onPageChange={(event, newPage) =>
                    handlePageChange(newPage + 1)
                  }
                  rowsPerPage={pageSize}
                  onRowsPerPageChange={(event) => {
                    dispatch(setPageSize(parseInt(event.target.value, 10)));
                    dispatch(setPage(1));
                  }}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              </TableContainer>

              {/* Stats at bottom */}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default DashboardPage;
